import React from 'react';
import { motion } from 'framer-motion';
import { Brain, ArrowUpRight, ShieldAlert, Sparkles, Database, Filter } from 'lucide-react';
import xtraceWordmark from '../assets/xtrace-wordmark.svg';
import MemorySpace from './MemorySpace';
import './MemoryPanel.css';

/**
 * Renders the XTrace memory activity for a verification run.
 *
 * The panel exists to make the memory layer auditable: which prior findings
 * were recalled, how strongly they matched, whether any claim was demoted for
 * lacking evidence from this run, and what was written back for next time.
 */
const MemoryPanel = ({ memory }) => {
  if (!memory || !memory.enabled) return null;

  const recalled = memory.recalled || [];
  const lessons = memory.lessons || [];
  const write = memory.write || {};
  const created = write.created || [];
  const usedInVerdict = memory.used_in_verdict || [];
  const demoted = memory.demoted_claims || 0;
  const conflict = (memory.conflict || '').trim();
  const topScore = memory.recall_top_score || 0;
  const filtered = memory.recall_filtered || 0;
  const articles = memory.articles || {};
  const ingested = articles.ingested || 0;

  const hasActivity =
    recalled.length > 0 || lessons.length > 0 || created.length > 0 || demoted > 0;

  return (
    <motion.div
      className="memory-panel glass-effect"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.45 }}
    >
      <div className="memory-header">
        <div className="memory-title">
          <Brain size={20} />
          <h3>Memory Layer</h3>
        </div>
        <a
          className="memory-badge"
          href="https://www.xtrace.ai/"
          target="_blank"
          rel="noopener noreferrer"
          title="Long-term agent memory by XTrace"
        >
          <img src={xtraceWordmark} alt="XTrace" />
        </a>
      </div>

      <p className="memory-intro">
        Retrieved articles are written into the memory space as they arrive, so source
        material that exceeds the context window becomes durable knowledge. Prior
        findings are recalled before analysis and new findings written back after.
      </p>

      <div className="memory-metrics">
        <div className="memory-metric">
          <span className="metric-value">{recalled.length}</span>
          <span className="metric-label">Recalled</span>
        </div>
        <div className="memory-metric">
          <span className="metric-value">
            {topScore ? `${Math.round(topScore * 100)}%` : '—'}
          </span>
          <span className="metric-label">Top match</span>
        </div>
        <div className="memory-metric">
          <span className="metric-value">{filtered}</span>
          <span className="metric-label">Filtered out</span>
        </div>
        <div className="memory-metric">
          <span className="metric-value">{ingested}</span>
          <span className="metric-label">Articles in</span>
        </div>
        <div className="memory-metric">
          <span className="metric-value">{created.length}</span>
          <span className="metric-label">Stored</span>
        </div>
        <div className="memory-metric">
          <span className="metric-value">{write.superseded_count || 0}</span>
          <span className="metric-label">Superseded</span>
        </div>
      </div>

      {filtered > 0 && (
        <div className="memory-alert info">
          <Filter size={16} />
          <span>
            {filtered} nearest-neighbour result{filtered === 1 ? '' : 's'} fell below the
            relevance threshold and {filtered === 1 ? 'was' : 'were'} discarded before
            reaching the model, keeping unrelated context out of the analysis.
          </span>
        </div>
      )}

      {/* Force-directed view: distance encodes relevance to this claim. */}
      <MemorySpace memory={memory} />

      {demoted > 0 && (
        <div className="memory-alert guard">
          <ShieldAlert size={16} />
          <span>
            {demoted} claim{demoted === 1 ? '' : 's'} cited no source retrieved in this
            run and {demoted === 1 ? 'was' : 'were'} demoted to unverified. Memory can
            corroborate a finding but never establish one on its own.
          </span>
        </div>
      )}

      {conflict && conflict.toLowerCase() !== 'none' && (
        <div className="memory-alert conflict">
          <ShieldAlert size={16} />
          <span>
            <strong>Memory conflict:</strong> {conflict} Today&rsquo;s sources take
            precedence.
          </span>
        </div>
      )}

      {recalled.length > 0 && (
        <div className="memory-section">
          <h4>Recalled prior findings</h4>
          <ul className="memory-list">
            {recalled.slice(0, 6).map((item, index) => (
              <li key={item.id || index} className="memory-item">
                <div className="memory-item-head">
                  <span className={`memory-type ${item.type || 'fact'}`}>
                    {item.type || 'fact'}
                  </span>
                  <span className="memory-score">
                    {Math.round((item.score || 0) * 100)}% match
                  </span>
                </div>
                <p className="memory-text">{item.text}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {lessons.length > 0 && (
        <div className="memory-section">
          <h4>
            <Sparkles size={14} /> Learned retrieval guidance
          </h4>
          <ul className="memory-list">
            {lessons.slice(0, 5).map((item, index) => (
              <li key={index} className="memory-item lesson">
                <p className="memory-text">{item.text}</p>
                {item.observation_count ? (
                  <span className="memory-meta">
                    confirmed {item.observation_count}&times;
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      {usedInVerdict.length > 0 && (
        <div className="memory-section">
          <h4>
            <ArrowUpRight size={14} /> Corroborated the verdict
          </h4>
          <ul className="memory-list compact">
            {usedInVerdict.slice(0, 5).map((item, index) => (
              <li key={index} className="memory-item">
                <p className="memory-text">{item}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {created.length > 0 && (
        <div className="memory-section">
          <h4>
            <Database size={14} /> Written back for future runs
          </h4>
          <ul className="memory-list compact">
            {created.slice(0, 6).map((item, index) => (
              <li key={item.id || index} className="memory-item stored">
                <span className={`memory-type ${item.type || 'fact'}`}>
                  {item.type || 'fact'}
                </span>
                <p className="memory-text">{item.text}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!hasActivity && (
        <p className="memory-empty">
          No prior knowledge matched this claim &mdash; this run establishes the
          baseline. Submitting a similar claim later will recall these findings.
        </p>
      )}
    </motion.div>
  );
};

export default MemoryPanel;
